/**
 * Earn Rules List Block - Frontend View
 *
 * Client-side rendering for all enrollment states.
 * Shows skeleton while loading, then renders appropriate state.
 */
import React, { useState, useEffect } from 'react';
import { createRoot } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { useEnrollment } from '@blocks/_shared/hooks/use-enrollment';
import { EarnRulesGridSkeleton } from '@blocks/_shared/skeleton/patterns/earn-rules';
import Button from '@blocks/_shared/button';
import { replaceStrings } from '@blocks/_shared/utils/replacers';
import { getTranslatedText } from '@blocks/_shared/utils/translated-text';
import { joinProgram } from '~/shared/queries';
import {
	Item,
	ItemContent,
	ItemMedia,
	ItemLabel,
	ItemMeta,
	ItemBadge,
	ItemActions,
	ItemError,
} from '@blocks/_shared/components';
import { EarnRulesProvider, useEarnRulesContext } from './context';
import './style.scss';

// ============================================================================
// Types
// ============================================================================

interface Attributes {
	columns: number;
	showClaimedRules: boolean;
	showToGuests: boolean;
	showRulesToNotEnrolled: boolean;
	cardLabelLevel: number;
	buttonTextJoin: string;
	buttonTextSignIn: string;
	buttonTextClaim: string;
	buttonTextClaimed: string;
	buttonTextAutomatic: string;
	emptyStateText: string;
	showCreditsBadge: boolean;
	// Simplified button colors (single attribute, direct hex/preset)
	buttonTextColor?: string;
	buttonBackgroundColor?: string;
}

interface EarnRule {
	id: number;
	type: { value: string };
	label: { value: string | Record<string, string> };
	credits: { value: number };
	svg: string;
	socialHandle?: { value: string };
}

interface ClaimedReward {
	earn_rule_id: string;
}

// ============================================================================
// Main Component
// ============================================================================

const EarnRulesListView = ({ attributes }: { attributes: Attributes }) => {
	const { status, creditsCurrency, claimedRewards, isLoading: isEnrollmentLoading, refetch } = useEnrollment();
	const [earnRules, setEarnRules] = useState<EarnRule[]>([]);
	const [isLoadingRules, setIsLoadingRules] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Fetch earn rules from API when needed based on status and settings
	useEffect(() => {
		const shouldFetchRules =
			status === 'enrolled' ||
			(status === 'guest' && attributes.showToGuests) ||
			(status === 'loggedInNotEnrolled' && attributes.showRulesToNotEnrolled);

		if (shouldFetchRules) {
			apiFetch<EarnRule[]>({ path: '/leat/v1/earn-rules' })
				.then(setEarnRules)
				.catch((err) => {
					// Check if this is a "user not enrolled" error (404 or specific error codes)
					// The error can be a Response object (with .status) or an error object (with .code/.data)
					const isNotEnrolledError =
						err.status === 404 || // Response object
						err.code === 'spend-rules-claim' ||
						err.code === 'no_contact_uuid' ||
						err.data?.status === 404 ||
						(err.message && err.message.toLowerCase().includes('not found')) ||
						(err.message && err.message.toLowerCase().includes('user not found'));

					if (isNotEnrolledError) {
						console.log('[Earn Rules] User not enrolled (404) - showing empty state');
						setEarnRules([]);
					} else {
						// Real error - show error message
						console.error('[Earn Rules] API Error:', err);
						setError(err.message || 'Failed to load earn rules');
					}
				})
				.finally(() => setIsLoadingRules(false));
		} else {
			setIsLoadingRules(false);
		}
	}, [status, attributes.showToGuests, attributes.showRulesToNotEnrolled]);

	// Handler to refresh enrollment data after claiming
	const handleRefreshNeeded = () => {
		if (refetch) {
			refetch();
		}
	};

	// Show skeleton while loading
	if (isEnrollmentLoading || (status === 'enrolled' && isLoadingRules)) {
		return <EarnRulesGridSkeleton columns={attributes.columns} />;
	}

	// Show error state
	if (error) {
		return (
			<div className="leat-earn-rules-list__error">
				<p>{error}</p>
				<Button onClick={() => window.location.reload()} variant="contained">
					Try Again
				</Button>
			</div>
		);
	}

	// Render based on enrollment status
	switch (status) {
		case 'guest':
			// Show grid to guests if enabled, otherwise show sign-in prompt
			if (attributes.showToGuests) {
				return (
					<EnrolledState
						attributes={attributes}
						earnRules={earnRules}
						claimedRewards={[]}
						creditsCurrency={creditsCurrency || 'points'}
						onRefreshNeeded={handleRefreshNeeded}
						isGuest={true}
					/>
				);
			}
			return <GuestState attributes={attributes} />;

		case 'loggedInNotEnrolled':
			return (
				<LoggedInNotEnrolledState
					attributes={attributes}
					creditsCurrency={creditsCurrency || 'points'}
					earnRules={earnRules}
				/>
			);

		case 'enrolled':
			return (
				<EnrolledState
					attributes={attributes}
					earnRules={earnRules}
					claimedRewards={claimedRewards || []}
					creditsCurrency={creditsCurrency || 'points'}
					onRefreshNeeded={handleRefreshNeeded}
					isGuest={false}
				/>
			);

		default:
			return null;
	}
};

// ============================================================================
// State Components
// ============================================================================

const GuestState = () => {
	// Use WooCommerce my-account page with redirect back to current page
	const myAccountUrl = window.leat?.storePages?.myaccount?.permalink || '/my-account/';
	const redirectUrl = `${myAccountUrl}?redirect_to=${encodeURIComponent(window.location.href)}`;

	return (
		<div className="leat-earn-rules-list__empty-state">
			<Button
				href={redirectUrl}
				variant="contained"
			>
				Sign In
			</Button>
		</div>
	);
};

const LoggedInNotEnrolledState = ({
	attributes,
	creditsCurrency,
	earnRules,
}: {
	attributes: Attributes;
	creditsCurrency: string;
	earnRules: EarnRule[];
}) => {
	const [isJoining, setIsJoining] = useState(false);
	const [joinError, setJoinError] = useState<string | null>(null);

	const handleJoinProgram = async () => {
		setIsJoining(true);
		setJoinError(null);

		try {
			// Get current user ID from window.leat
			const userId = window.leat?.userId ?? null;

			if (!userId) {
				throw new Error('User ID not found');
			}

			await joinProgram(userId);

			// Reload the page to refresh enrollment status
			window.location.reload();
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to join program';
			setJoinError(message);
			setIsJoining(false);
		}
	};

	// If showRulesToNotEnrolled is enabled, show the grid with Join Program buttons
	if (attributes.showRulesToNotEnrolled) {
		// Simple button color extraction
		const buttonInlineStyles: React.CSSProperties = {};
		if (attributes.buttonTextColor) {
			buttonInlineStyles.color = attributes.buttonTextColor;
		}
		if (attributes.buttonBackgroundColor) {
			buttonInlineStyles.backgroundColor = attributes.buttonBackgroundColor;
			buttonInlineStyles.borderColor = 'transparent';
		}

		// Create context value for all cards
		const contextValue = {
			claimedRewards: [],
			creditsCurrency,
			labelLevel: attributes.cardLabelLevel,
			isGuest: false,
			onClaimSuccess: () => {},
			buttonTexts: {
				join: attributes.buttonTextJoin,
				signIn: attributes.buttonTextSignIn,
				claim: attributes.buttonTextClaim,
				claimed: attributes.buttonTextClaimed,
				automatic: attributes.buttonTextAutomatic,
			},
			buttonInlineStyles,
			showCreditsBadge: attributes.showCreditsBadge,
			onJoinProgram: handleJoinProgram,
			isJoiningProgram: isJoining,
			joinProgramError: joinError,
		};

		return (
			<EarnRulesProvider value={contextValue}>
				<div
					className="leat-earn-rules-list__grid"
					style={{
						gridTemplateColumns: `repeat(${attributes.columns}, 1fr)`,
					}}
				>
					{earnRules.map((rule) => (
						<EarnRuleCard key={rule.id} rule={rule} showJoinButton={true} />
					))}
				</div>
			</EarnRulesProvider>
		);
	}

	// Default: show empty state (pattern will provide heading/description)
	return null;
};

const EnrolledState = ({
	attributes,
	earnRules,
	claimedRewards,
	creditsCurrency,
	onRefreshNeeded,
	isGuest = false,
}: {
	attributes: Attributes;
	earnRules: EarnRule[];
	claimedRewards: ClaimedReward[];
	creditsCurrency: string;
	onRefreshNeeded: () => void;
	isGuest?: boolean;
}) => {
	if (earnRules.length === 0) {
		return (
			<div className="leat-earn-rules-list__empty-state">
				<p>{attributes.emptyStateText}</p>
			</div>
		);
	}

	// Note: We always show all rules now. The card component handles showing "Claimed" badge.
	// The showClaimedRules attribute controls whether to hide claimed rules entirely.
	const displayRules = attributes.showClaimedRules
		? earnRules // Show all rules including claimed
		: earnRules.filter((rule) => {
				// Only filter out social rules that have been claimed
				const socialTypes = [
					'LIKE_ON_FACEBOOK',
					'FOLLOW_ON_INSTAGRAM',
					'FOLLOW_ON_TIKTOK',
					'CREATE_ACCOUNT',
				];
				if (!socialTypes.includes(rule.type.value)) return true; // Always show non-social rules

				// Filter out claimed social rules only if showClaimedRules is false
				return !claimedRewards.find((r) => r.earn_rule_id === rule.id.toString());
		  });

	// Simple button color extraction
	const buttonInlineStyles: React.CSSProperties = {};
	if (attributes.buttonTextColor) {
		buttonInlineStyles.color = attributes.buttonTextColor;
	}
	if (attributes.buttonBackgroundColor) {
		buttonInlineStyles.backgroundColor = attributes.buttonBackgroundColor;
	}

	// Create context value for all cards
	const contextValue = {
		claimedRewards,
		creditsCurrency,
		labelLevel: attributes.cardLabelLevel,
		isGuest,
		onClaimSuccess: onRefreshNeeded,
		buttonTexts: {
			join: attributes.buttonTextJoin,
			signIn: attributes.buttonTextSignIn,
			claim: attributes.buttonTextClaim,
			claimed: attributes.buttonTextClaimed,
			automatic: attributes.buttonTextAutomatic,
		},
		buttonInlineStyles,
		showCreditsBadge: attributes.showCreditsBadge,
	};

	const gridStyle: React.CSSProperties = {
		gridTemplateColumns: `repeat(${attributes.columns}, 1fr)`,
	};

	return (
		<EarnRulesProvider value={contextValue}>
			<div className="leat-earn-rules-list__grid" style={gridStyle}>
				{displayRules.map((rule) => (
					<EarnRuleCard key={rule.id} rule={rule} />
				))}
			</div>
		</EarnRulesProvider>
	);
};

// ============================================================================
// Earn Rule Card
// ============================================================================

const EarnRuleCard = ({
	rule,
	showJoinButton = false,
}: {
	rule: EarnRule;
	showJoinButton?: boolean;
}) => {
	// Get shared context instead of props
	const {
		claimedRewards,
		creditsCurrency,
		labelLevel,
		isGuest,
		onClaimSuccess,
		buttonTexts,
		buttonInlineStyles,
		showCreditsBadge,
		onJoinProgram,
		isJoiningProgram = false,
		joinProgramError,
	} = useEarnRulesContext();
	const [isClaiming, setIsClaiming] = useState(false);
	const [claimError, setClaimError] = useState<string | null>(null);
	const [localHasClaimed, setLocalHasClaimed] = useState(false);

	const socialTypes = ['LIKE_ON_FACEBOOK', 'FOLLOW_ON_INSTAGRAM', 'FOLLOW_ON_TIKTOK', 'CREATE_ACCOUNT'];
	const isSocial = socialTypes.includes(rule.type.value);
	const hasClaimed = localHasClaimed || claimedRewards.find((r) => r.earn_rule_id === rule.id.toString());

	const handleClaim = async () => {
		setIsClaiming(true);
		setClaimError(null);

		try {
			// Call the claim API (userId defaults to current user on backend)
			await apiFetch({
				path: '/leat/v1/earn-reward',
				method: 'POST',
				data: {
					earnRuleId: rule.id,
				},
			});

			// Mark as claimed locally
			setLocalHasClaimed(true);

			// Open social link if applicable
			const handle = rule.socialHandle?.value;
			if (handle) {
				const socialLink = getSocialLink(rule.type.value, handle);
				if (socialLink) {
					window.open(socialLink, '_blank');
				}
			}

			// Notify parent to refresh data
			onClaimSuccess();
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to claim reward';
			setClaimError(message);
		} finally {
			setIsClaiming(false);
		}
	};

	// Get translated label text
	const labelValue = rule.label.value;
	const label = typeof labelValue === 'string' ? labelValue : getTranslatedText(labelValue);
	const credits = rule.credits.value;

	// For rules without fixed credits (like PLACE_ORDER), show the label without credit amount
	const displayLabel = credits !== null
		? replaceStrings(label, [
				{
					'{{credits}}': credits.toString(),
					'{{credits_currency}}': creditsCurrency,
				},
		  ])
		: replaceStrings(label, [{ '{{credits_currency}}': creditsCurrency }]);

	// Create dynamic heading tag based on labelLevel
	const LabelTag = `h${labelLevel}` as keyof JSX.IntrinsicElements;

	// Generate redirect URL for login/join actions
	const myAccountUrl = window.leat?.storePages?.myaccount?.permalink || '/my-account/';
	const redirectUrl = `${myAccountUrl}?redirect_to=${encodeURIComponent(window.location.href)}`;

	return (
		<Item variant="default">
			{/* Badge in top right corner */}
			{showCreditsBadge && credits !== null && (
				<ItemBadge>
					+{credits} {creditsCurrency}
				</ItemBadge>
			)}

			<ItemContent>
				<ItemMedia>
					{/* biome-ignore lint/security/noDangerouslySetInnerHtml: This is controlled SVG content from backend */}
					<div dangerouslySetInnerHTML={{ __html: rule.svg }} />
				</ItemMedia>
				<ItemLabel as={LabelTag}>{displayLabel}</ItemLabel>
				{/* Show meta below label only if badge is disabled */}
				{!showCreditsBadge && credits !== null && (
					<ItemMeta variant="credits">
						+{credits} {creditsCurrency}
					</ItemMeta>
				)}
			</ItemContent>

			{/* Action zone: Always present for consistent card height */}
			<ItemActions>
				{showJoinButton ? (
					// Show Join Program button for non-enrolled users
					<>
						<Button
							onClick={onJoinProgram}
							disabled={isJoiningProgram}
							variant="contained"
							className="leat-button--small"
							style={buttonInlineStyles}
						>
							{isJoiningProgram ? 'Joining...' : buttonTexts.join}
						</Button>
						{joinProgramError && <ItemError>{joinProgramError}</ItemError>}
					</>
				) : isSocial ? (
					// Claimable social actions
					isGuest ? (
						// Guest user - show sign in prompt
						<Button
							href={redirectUrl}
							variant="contained"
							className="leat-button--small"
							style={buttonInlineStyles}
						>
							{buttonTexts.signIn}
						</Button>
					) : hasClaimed ? (
						// Logged in & claimed - show claimed badge
						<Button
							renderAs="span"
							variant="text"
							className="leat-button--small leat-button--claimed"
							removeTextWrap
						>
							<CheckCircleIcon />
							<span>{buttonTexts.claimed}</span>
						</Button>
					) : (
						// Logged in & not claimed - show claim button
						<>
							<Button
								onClick={handleClaim}
								disabled={isClaiming}
								variant="contained"
								className="leat-button--small"
								style={buttonInlineStyles}
							>
								{isClaiming ? 'Claiming...' : buttonTexts.claim}
							</Button>
							{claimError && <ItemError>{claimError}</ItemError>}
						</>
					)
				) : (
					// Automatic tracking indicator
					<Button
						renderAs="span"
						variant="text"
						className="leat-button--small leat-button--automatic"
						removeTextWrap
					>
						<AutomaticIcon />
						<span>{buttonTexts.automatic}</span>
					</Button>
				)}
			</ItemActions>
		</Item>
	);
};

// ============================================================================
// Helpers
// ============================================================================

const getSocialLink = (type: string, handle: string): string => {
	switch (type) {
		case 'LIKE_ON_FACEBOOK':
			return `https://www.facebook.com/${handle}`;
		case 'FOLLOW_ON_INSTAGRAM':
			return `https://www.instagram.com/${handle}`;
		case 'FOLLOW_ON_TIKTOK':
			return `https://www.tiktok.com/@${handle}`;
		default:
			return '';
	}
};

const CheckCircleIcon = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
		<circle cx="12" cy="12" r="10" />
		<path d="M9 12l2 2 4-4" />
	</svg>
);

const AutomaticIcon = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
	</svg>
);

// ============================================================================
// Initialization
// ============================================================================

export function initEarnRulesListBlocks() {
	// Hydrate ALL blocks regardless of enrollment status
	const blocks = document.querySelectorAll<HTMLElement>('.leat-earn-rules-list');

	for (const block of blocks) {
		const attributes: Attributes = {
			columns: Number(block.dataset.columns) || 3,
			showClaimedRules: block.dataset.showClaimed === 'true',
			showToGuests: block.dataset.showToGuests !== 'false', // Default to true
			showRulesToNotEnrolled: block.dataset.showRulesToNotEnrolled !== 'false', // Default to true
			cardLabelLevel: Number(block.dataset.cardLabelLevel) || 4,
			buttonTextJoin: block.dataset.buttonTextJoin || 'Join Program',
			buttonTextSignIn: block.dataset.buttonTextSignIn || 'Sign In to Claim',
			buttonTextClaim: block.dataset.buttonTextClaim || 'Claim',
			buttonTextClaimed: block.dataset.buttonTextClaimed || 'Claimed',
			buttonTextAutomatic: block.dataset.buttonTextAutomatic || 'Automatic',
			emptyStateText: block.dataset.emptyStateText || 'No earning opportunities available at this time.',
			showCreditsBadge: block.dataset.showCreditsBadge !== 'false', // Default to true
			buttonTextColor: block.dataset.buttonTextColor || undefined,
			buttonBackgroundColor: block.dataset.buttonBackgroundColor || undefined,
		};

		createRoot(block).render(<EarnRulesListView attributes={attributes} />);
	}
}

// Auto-initialize
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initEarnRulesListBlocks);
} else {
	initEarnRulesListBlocks();
}
